import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ComponentPropsWithoutRef,
  type ReactNode,
} from "react";
import { createPortal } from "react-dom";

export const TOOLTIP_OPEN_DELAY_MS = 120;

export type TooltipPlacement = "top" | "right" | "bottom" | "left";

interface Rect {
  readonly top: number;
  readonly right: number;
  readonly bottom: number;
  readonly left: number;
  readonly width: number;
  readonly height: number;
}

interface Viewport {
  readonly width: number;
  readonly height: number;
}

export interface TooltipPosition {
  readonly placement: TooltipPlacement;
  readonly top: number;
  readonly left: number;
}

const tooltipGap = 8;
const viewportMargin = 8;

const placementOrder: Readonly<Record<TooltipPlacement, readonly TooltipPlacement[]>> = {
  top: ["top", "bottom", "right", "left"],
  right: ["right", "left", "bottom", "top"],
  bottom: ["bottom", "top", "right", "left"],
  left: ["left", "right", "bottom", "top"],
};

/** Positions a tooltip beside its trigger while staying inside the visible viewport. */
export function resolveTooltipPosition(
  trigger: Rect,
  tooltip: Rect,
  viewport: Viewport,
  preferred: TooltipPlacement,
): TooltipPosition {
  const placement =
    placementOrder[preferred].find((candidate) =>
      hasPrimaryAxisSpace(candidate, trigger, tooltip, viewport),
    ) ?? preferred;
  const raw = rawPosition(placement, trigger, tooltip);
  return {
    placement,
    top: clamp(raw.top, viewportMargin, viewport.height - tooltip.height - viewportMargin),
    left: clamp(raw.left, viewportMargin, viewport.width - tooltip.width - viewportMargin),
  };
}

function hasPrimaryAxisSpace(
  placement: TooltipPlacement,
  trigger: Rect,
  tooltip: Rect,
  viewport: Viewport,
): boolean {
  if (placement === "top") {
    return trigger.top - tooltipGap - tooltip.height >= viewportMargin;
  }
  if (placement === "right") {
    return trigger.right + tooltipGap + tooltip.width <= viewport.width - viewportMargin;
  }
  if (placement === "bottom") {
    return trigger.bottom + tooltipGap + tooltip.height <= viewport.height - viewportMargin;
  }
  return trigger.left - tooltipGap - tooltip.width >= viewportMargin;
}

function rawPosition(placement: TooltipPlacement, trigger: Rect, tooltip: Rect) {
  if (placement === "top") {
    return {
      top: trigger.top - tooltipGap - tooltip.height,
      left: trigger.left + (trigger.width - tooltip.width) / 2,
    };
  }
  if (placement === "right") {
    return {
      top: trigger.top + (trigger.height - tooltip.height) / 2,
      left: trigger.right + tooltipGap,
    };
  }
  if (placement === "bottom") {
    return {
      top: trigger.bottom + tooltipGap,
      left: trigger.left + (trigger.width - tooltip.width) / 2,
    };
  }
  return {
    top: trigger.top + (trigger.height - tooltip.height) / 2,
    left: trigger.left - tooltipGap - tooltip.width,
  };
}

function clamp(value: number, minimum: number, maximum: number): number {
  return Math.min(Math.max(value, minimum), Math.max(minimum, maximum));
}

interface TooltipContextValue {
  readonly activeId: string | null;
  readonly cancel: (id: string) => void;
  readonly hide: (id: string) => void;
  readonly schedule: (id: string) => void;
  readonly show: (id: string) => void;
}

const TooltipContext = createContext<TooltipContextValue | null>(null);

/** Coordinates app tooltips so only the current icon action is described visually. */
export function TooltipProvider({ children }: { readonly children: ReactNode }) {
  const [activeId, setActiveId] = useState<string | null>(null);
  const activeIdRef = useRef<string | null>(null);
  const pendingIdRef = useRef<string | null>(null);
  const openTimerRef = useRef<number | null>(null);

  const setActive = useCallback((id: string | null): void => {
    activeIdRef.current = id;
    setActiveId(id);
  }, []);

  const cancel = useCallback((id: string): void => {
    if (pendingIdRef.current !== id) {
      return;
    }
    if (openTimerRef.current !== null) {
      window.clearTimeout(openTimerRef.current);
      openTimerRef.current = null;
    }
    pendingIdRef.current = null;
  }, []);

  const cancelPending = useCallback((): void => {
    if (openTimerRef.current !== null) {
      window.clearTimeout(openTimerRef.current);
      openTimerRef.current = null;
    }
    pendingIdRef.current = null;
  }, []);

  const show = useCallback(
    (id: string): void => {
      cancelPending();
      setActive(id);
    },
    [cancelPending, setActive],
  );

  const schedule = useCallback(
    (id: string): void => {
      cancelPending();
      pendingIdRef.current = id;
      openTimerRef.current = window.setTimeout(() => {
        openTimerRef.current = null;
        pendingIdRef.current = null;
        setActive(id);
      }, TOOLTIP_OPEN_DELAY_MS);
    },
    [cancelPending, setActive],
  );

  const hide = useCallback(
    (id: string): void => {
      cancel(id);
      if (activeIdRef.current === id) {
        setActive(null);
      }
    },
    [cancel, setActive],
  );

  const dismiss = useCallback((): void => {
    cancelPending();
    setActive(null);
  }, [cancelPending, setActive]);

  const value = useMemo(
    () => ({ activeId, cancel, hide, schedule, show }),
    [activeId, cancel, hide, schedule, show],
  );

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent): void => {
      if (
        event.key !== "Escape" ||
        (activeIdRef.current === null && pendingIdRef.current === null)
      ) {
        return;
      }
      event.preventDefault();
      event.stopImmediatePropagation();
      dismiss();
    };
    document.addEventListener("keydown", handleKeyDown, true);
    return () => {
      document.removeEventListener("keydown", handleKeyDown, true);
      cancelPending();
    };
  }, [cancelPending, dismiss]);

  return <TooltipContext.Provider value={value}>{children}</TooltipContext.Provider>;
}

type NativeButtonProps = Omit<
  ComponentPropsWithoutRef<"button">,
  | "aria-describedby"
  | "aria-label"
  | "children"
  | "onBlur"
  | "onFocus"
  | "onMouseEnter"
  | "onMouseLeave"
  | "onPointerDown"
  | "onPointerUp"
  | "title"
>;

export interface IconButtonProps extends NativeButtonProps {
  readonly children: ReactNode;
  readonly label: string;
  readonly tooltipPlacement?: TooltipPlacement;
}

/** Renders an accessible icon-only action with a fast, viewport-aware tooltip. */
export function IconButton({
  children,
  label,
  tooltipPlacement = "bottom",
  type = "button",
  ...buttonProps
}: IconButtonProps) {
  const context = useContext(TooltipContext);
  const id = `tooltip-${useId().replaceAll(":", "")}`;
  const triggerRef = useRef<HTMLButtonElement>(null);
  const tooltipRef = useRef<HTMLSpanElement>(null);
  const openTimerRef = useRef<number | null>(null);
  const hoveredRef = useRef(false);
  const focusedRef = useRef(false);
  const pointerFocusRef = useRef(false);
  const [localVisible, setLocalVisible] = useState(false);
  const [position, setPosition] = useState<TooltipPosition | null>(null);
  const visible = context === null ? localVisible : context.activeId === id;
  const contextCancel = context?.cancel;
  const contextShow = context?.show;
  const contextHide = context?.hide;
  const contextSchedule = context?.schedule;
  const portalTarget = document.querySelector("main") ?? document.body;

  const show = useCallback(() => {
    if (contextShow === undefined) {
      setLocalVisible(true);
    } else {
      contextShow(id);
    }
  }, [contextShow, id]);

  const hide = useCallback(() => {
    if (contextHide === undefined) {
      setLocalVisible(false);
    } else {
      contextHide(id);
    }
  }, [contextHide, id]);

  const clearOpenTimer = useCallback(() => {
    if (openTimerRef.current !== null) {
      window.clearTimeout(openTimerRef.current);
      openTimerRef.current = null;
    }
  }, []);

  const cancel = useCallback(() => {
    clearOpenTimer();
    contextCancel?.(id);
  }, [clearOpenTimer, contextCancel, id]);

  const schedule = useCallback(() => {
    if (contextSchedule === undefined) {
      clearOpenTimer();
      openTimerRef.current = window.setTimeout(show, TOOLTIP_OPEN_DELAY_MS);
    } else {
      contextSchedule(id);
    }
  }, [clearOpenTimer, contextSchedule, id, show]);

  const updatePosition = useCallback(() => {
    const trigger = triggerRef.current;
    const tooltip = tooltipRef.current;
    if (trigger === null || tooltip === null) {
      return;
    }
    setPosition(
      resolveTooltipPosition(
        trigger.getBoundingClientRect(),
        tooltip.getBoundingClientRect(),
        { width: window.innerWidth, height: window.innerHeight },
        tooltipPlacement,
      ),
    );
  }, [tooltipPlacement]);

  useLayoutEffect(() => {
    if (!visible) {
      return;
    }
    updatePosition();
    window.addEventListener("resize", updatePosition);
    window.addEventListener("scroll", updatePosition, true);
    return () => {
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition, true);
    };
  }, [visible, updatePosition]);

  useEffect(
    () => () => {
      cancel();
      hide();
    },
    [cancel, hide],
  );

  return (
    <>
      <button
        {...buttonProps}
        ref={triggerRef}
        type={type}
        aria-label={label}
        aria-describedby={visible ? id : undefined}
        onMouseEnter={() => {
          hoveredRef.current = true;
          schedule();
        }}
        onMouseLeave={() => {
          hoveredRef.current = false;
          cancel();
          if (!focusedRef.current) {
            hide();
          }
        }}
        onFocus={() => {
          focusedRef.current = true;
          cancel();
          if (pointerFocusRef.current) {
            pointerFocusRef.current = false;
          } else {
            show();
          }
        }}
        onBlur={() => {
          focusedRef.current = false;
          if (!hoveredRef.current) {
            hide();
          }
        }}
        onPointerDown={() => {
          pointerFocusRef.current = true;
          cancel();
          hide();
        }}
        onPointerUp={() => {
          pointerFocusRef.current = false;
        }}
      >
        {children}
      </button>
      {visible
        ? createPortal(
            <span
              ref={tooltipRef}
              id={id}
              className="app-tooltip"
              role="tooltip"
              data-placement={position?.placement ?? tooltipPlacement}
              data-ready={position === null ? "false" : "true"}
              style={position === null ? undefined : { top: position.top, left: position.left }}
            >
              {label}
            </span>,
            portalTarget,
          )
        : null}
    </>
  );
}
